import React, { useMemo } from 'react';
import { AppInsightsContext, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { initAppInsights, InitAppInsightsOptions, getReactPlugin } from './appInsights';

interface AppInsightsProviderProps extends InitAppInsightsOptions {
	children: React.ReactNode;
	/** Component rendered when a descendant throws. Defaults to a minimal message. */
	errorFallback?: React.ComponentType;
}

const DefaultErrorFallback: React.ComponentType = () => (
	<div role="alert">Something went wrong. The error has been reported.</div>
);

/**
 * Bootstraps Application Insights and wires its React plugin context plus an
 * error boundary so descendants can call useAppInsightsContext / useTrackEvent
 * and unhandled render errors are automatically reported.
 */
export const AppInsightsProvider: React.FC<AppInsightsProviderProps> = ({ children, errorFallback, ...options }) => {
	const plugin = useMemo(() => {
		initAppInsights(options);
		return getReactPlugin();
		// Options are read once at mount; treat as init-only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!plugin) {
		return <>{children}</>;
	}

	return (
		<AppInsightsContext.Provider value={plugin}>
			<AppInsightsErrorBoundary onError={errorFallback ?? DefaultErrorFallback} appInsights={plugin}>
				<>{children}</>
			</AppInsightsErrorBoundary>
		</AppInsightsContext.Provider>
	);
};
